import { c as _c } from "react/compiler-runtime";
import { CardElement, Elements, useElements, useStripe as useStripeElements } from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import { CreditCard, ExternalLink, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useApi } from "@/lib/fetch-client";
import { useStripe } from "@/lib/stripe";
import { useI18n } from "@llmgateway/i18n/react";
import { CREDIT_TOP_UP_MAX_AMOUNT, CREDIT_TOP_UP_MIN_AMOUNT, findPaymentProviderPublicConfig, hasEnabledPaymentProviderCapability, isCreditTopUpAmountInRange, redirectToCheckout } from "@llmgateway/shared";
import type React from "react";
function formatUsdAmount(amount: number): string {
  return `$${amount.toLocaleString("en-US")}`;
}
export function TopUpCreditsButton() {
  const $ = _c(5);
  const {
    t
  } = useI18n();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Plus className="mr-2 h-4 w-4" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== t) {
    t1 = t("billing.topUpCredits");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <TopUpCreditsDialog><Button className="flex items-center">{t0}{t1}</Button></TopUpCreditsDialog>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
interface TopUpCreditsDialogProps {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // The org credits should be added to (the playground's Chat org). When
  // omitted, the API falls back to the user's first organization.
  organizationId?: string;
}
export function TopUpCreditsDialog({
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  organizationId
}: TopUpCreditsDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledOnOpenChange ?? setInternalOpen;
  const [step, setStep] = useState<"amount" | "payment" | "select-payment" | "confirm-payment" | "success">("amount");
  const [amount, setAmount] = useState<number>(50);
  const [loading, setLoading] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    stripe,
    isLoading: stripeLoading,
    error: stripeError,
    enabled: stripeEnabled
  } = useStripe();
  const {
    t
  } = useI18n();
  const {
    data: paymentProvidersData
  } = api.useQuery("get", "/payments/providers", {}, {
    enabled: open
  });
  const stripeProvider = findPaymentProviderPublicConfig(paymentProvidersData?.providers, "stripe");
  const alipayProvider = findPaymentProviderPublicConfig(paymentProvidersData?.providers, "alipay");
  const stripeCardEnabled = Boolean(stripeEnabled && hasEnabledPaymentProviderCapability(stripeProvider, "creditTopUp"));
  const stripeSavedPaymentMethodsEnabled = Boolean(stripeEnabled && hasEnabledPaymentProviderCapability(stripeProvider, "savedPaymentMethod"));
  const stripeCheckoutEnabled = Boolean(stripeEnabled && hasEnabledPaymentProviderCapability(stripeProvider, "creditTopUp") && hasEnabledPaymentProviderCapability(stripeProvider, "redirectCheckout"));
  const alipayCheckoutEnabled = Boolean(hasEnabledPaymentProviderCapability(alipayProvider, "creditTopUp") && hasEnabledPaymentProviderCapability(alipayProvider, "redirectCheckout"));
  const {
    data: paymentMethodsData,
    isLoading: paymentMethodsLoading
  } = api.useQuery("get", "/payments/payment-methods", {}, {
    enabled: open && stripeSavedPaymentMethodsEnabled
  });
  const hasPaymentMethods = paymentMethodsData?.paymentMethods && paymentMethodsData.paymentMethods.length > 0;
  const defaultPaymentMethod = paymentMethodsData?.paymentMethods?.find(pm => pm.isDefault);
  useEffect(() => {
    if (defaultPaymentMethod) {
      setSelectedPaymentMethod(defaultPaymentMethod.id);
    }
  }, [defaultPaymentMethod]);
  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setStep("amount");
      setLoading(false);
    }, 300);
  };
  const handlePaymentSuccess = async () => {
    // Invalidate organization query to refresh credits balance
    await queryClient.invalidateQueries({
      queryKey: api.queryOptions("get", "/orgs").queryKey
    });
    setStep("success");
  };
  return <Dialog open={open} onOpenChange={setOpen}>
			{children ? <DialogTrigger asChild>{children}</DialogTrigger> : null}
			<DialogContent className="sm:max-w-[500px]">
				{step === "amount" ? <AmountStep amount={amount} setAmount={setAmount} organizationId={organizationId} stripeCardEnabled={stripeCardEnabled} stripeCheckoutEnabled={stripeCheckoutEnabled} alipayCheckoutEnabled={alipayCheckoutEnabled} onNext={() => {
        if (paymentMethodsLoading) {
          return; // Don't proceed if still loading
        }
        if (stripeSavedPaymentMethodsEnabled && hasPaymentMethods) {
          setStep("select-payment");
        } else {
          setStep("payment");
        }
      }} onCancel={handleClose} /> : step === "select-payment" ? <SelectPaymentStep amount={amount} paymentMethods={paymentMethodsData?.paymentMethods ?? []} selectedPaymentMethod={selectedPaymentMethod} setSelectedPaymentMethod={setSelectedPaymentMethod} onUseSelected={() => setStep("confirm-payment")} onAddNew={() => setStep("payment")} onBack={() => setStep("amount")} onCancel={handleClose} /> : step === "confirm-payment" ? <ConfirmPaymentStep amount={amount} paymentMethodId={selectedPaymentMethod!} organizationId={organizationId} onSuccess={handlePaymentSuccess} onBack={() => setStep("select-payment")} onCancel={handleClose} setLoading={setLoading} loading={loading} /> : step === "payment" ? !stripeCardEnabled ? <div className="p-6 text-center text-sm text-muted-foreground">
							{t("billing.cardPaymentsUnavailable")}
						</div> : stripeLoading ? <div className="p-6 text-center">
							{t("billing.loadingPaymentForm")}
						</div> : stripeError ? <div className="p-6 text-center text-sm text-destructive">
							{stripeError.message}
						</div> : <Elements stripe={stripe as any}>
							<PaymentStep amount={amount} organizationId={organizationId} onBack={() => setStep("amount")} onSuccess={handlePaymentSuccess} onCancel={handleClose} setLoading={setLoading} loading={loading} />
						</Elements> : <SuccessStep onClose={handleClose} />}
			</DialogContent>
		</Dialog>;
}
function AmountStep({
  amount,
  setAmount,
  organizationId,
  stripeCardEnabled,
  stripeCheckoutEnabled,
  alipayCheckoutEnabled,
  onNext,
  onCancel
}: {
  amount: number;
  setAmount: (amount: number) => void;
  organizationId?: string;
  stripeCardEnabled: boolean;
  stripeCheckoutEnabled: boolean;
  alipayCheckoutEnabled: boolean;
  onNext: () => void;
  onCancel: () => void;
}) {
  const presetAmounts = [10, 25, 50, 100];
  const api = useApi();
  const {
    t
  } = useI18n();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutProvider, setCheckoutProvider] = useState<"stripe" | "alipay" | null>(null);
  const {
    mutateAsync: createCheckoutSession
  } = api.useMutation("post", "/payments/create-checkout-session");
  const isAmountValid = isCreditTopUpAmountInRange(amount);
  const amountValidationMessage = amount > CREDIT_TOP_UP_MAX_AMOUNT ? t("billing.maximumTopUp", {
    amount: formatUsdAmount(CREDIT_TOP_UP_MAX_AMOUNT)
  }) : amount < CREDIT_TOP_UP_MIN_AMOUNT ? t("billing.minimumTopUp", {
    amount: formatUsdAmount(CREDIT_TOP_UP_MIN_AMOUNT)
  }) : !Number.isInteger(amount) ? t("billing.amountWholeDollar") : null;
  const {
    data: feeData,
    isLoading: feeDataLoading
  } = api.useQuery("post", "/payments/calculate-fees", {
    body: {
      amount
    }
  }, {
    enabled: isAmountValid
  });
  const isActionDisabled = !isAmountValid || Boolean(feeDataLoading) || checkoutLoading;
  const showHostedCheckoutDivider = stripeCardEnabled && (stripeCheckoutEnabled || alipayCheckoutEnabled);
  const singleHostedCheckoutProvider: "stripe" | "alipay" | null = !stripeCardEnabled && Number(stripeCheckoutEnabled) + Number(alipayCheckoutEnabled) === 1 ? alipayCheckoutEnabled ? "alipay" : "stripe" : null;
  const handleHostedCheckout = async (provider: "stripe" | "alipay") => {
    setCheckoutProvider(provider);
    setCheckoutLoading(true);
    try {
      const response = await createCheckoutSession({
        body: {
          amount,
          returnUrl: window.location.href.split("?")[0],
          provider,
          ...(organizationId ? {
            organizationId
          } : {})
        }
      });
      redirectToCheckout(response);
    } catch (error: unknown) {
      toast.error(t("billing.checkoutFailed"), {
        description: error instanceof Error ? error.message : t("billing.checkoutFailedDescription")
      });
      setCheckoutLoading(false);
      setCheckoutProvider(null);
    }
  };
  return <>
			<DialogHeader>
				<DialogTitle>{t("billing.topUpCredits")}</DialogTitle>
				<DialogDescription>
					{t("billing.addCreditsDescription")}
				</DialogDescription>
			</DialogHeader>
			<div className="space-y-4 py-4">
				<div className="space-y-2">
					<Label htmlFor="amount">{t("billing.amountUsd")}</Label>
					<Input id="amount" type="number" min={CREDIT_TOP_UP_MIN_AMOUNT} max={CREDIT_TOP_UP_MAX_AMOUNT} step={1} value={amount} onChange={e => setAmount(Number(e.target.value))} required />
					<p className="text-xs text-muted-foreground">
						{t("billing.minMaxTopUp", {
            min: formatUsdAmount(CREDIT_TOP_UP_MIN_AMOUNT),
            max: formatUsdAmount(CREDIT_TOP_UP_MAX_AMOUNT)
          })}
					</p>
					{amountValidationMessage ? <p className="text-xs text-destructive">
							{amountValidationMessage}
						</p> : null}
				</div>
				<div className="flex flex-wrap gap-2">
					{presetAmounts.map(preset => <Button key={preset} type="button" variant="outline" onClick={() => setAmount(preset)}>
							${preset}
						</Button>)}
				</div>

				{isAmountValid && <div className="border rounded-lg p-4 bg-muted/50">
						<p className="font-medium mb-2">{t("billing.feeBreakdown")}</p>
						{feeDataLoading ? <div className="flex items-center justify-center py-4">
								<div className="h-5 w-5 animate-spin border-2 border-muted-foreground border-t-transparent rounded-full" />
								<span className="ml-2 text-sm text-muted-foreground">
									{t("billing.calculatingFees")}
								</span>
							</div> : feeData ? <div className="space-y-1 text-sm">
								<div className="flex justify-between">
									<span>{t("billing.credits")}</span>
									<span>${feeData.baseAmount.toFixed(2)}</span>
								</div>
								<div className="flex justify-between">
									<span>{t("billing.platformFee")}</span>
									<span>${feeData.platformFee.toFixed(2)}</span>
								</div>
								{feeData.internationalFee > 0 ? <div className="flex justify-between">
										<span>{t("billing.internationalCardFee")}</span>
										<span>${feeData.internationalFee.toFixed(2)}</span>
									</div> : null}
								<div className="border-t pt-1 flex justify-between font-medium">
									<span>{t("billing.total")}</span>
									<span>${feeData.totalAmount.toFixed(2)}</span>
								</div>
							</div> : null}
					</div>}
			</div>
			<DialogFooter className="flex flex-col gap-3 sm:flex-col">
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onCancel}>
						{t("common.cancel")}
					</Button>
					{stripeCardEnabled ? <Button type="button" onClick={onNext} disabled={isActionDisabled}>
							{t("billing.payWithCard")}
						</Button> : null}
					{singleHostedCheckoutProvider ? <Button type="button" onClick={() => handleHostedCheckout(singleHostedCheckoutProvider)} disabled={isActionDisabled}>
							{checkoutLoading && checkoutProvider === singleHostedCheckoutProvider ? t("common.redirecting") : singleHostedCheckoutProvider === "alipay" ? t("billing.payWithAlipay") : t("billing.payWithStripeCheckout")}
						</Button> : null}
				</div>
				{showHostedCheckoutDivider ? <div className="relative">
						<div className="absolute inset-0 flex items-center">
							<span className="w-full border-t" />
						</div>
						<div className="relative flex justify-center text-xs uppercase">
							<span className="bg-background px-2 text-muted-foreground">
								{t("common.or")}
							</span>
						</div>
					</div> : null}
				{stripeCheckoutEnabled ? <Button type="button" variant="outline" className="w-full" onClick={() => handleHostedCheckout("stripe")} disabled={isActionDisabled}>
						{checkoutLoading && checkoutProvider === "stripe" ? t("common.redirecting") : <>
								<ExternalLink className="mr-2 h-4 w-4" />
								{t("billing.payWithStripeCheckout")}
							</>}
					</Button> : null}
				{alipayCheckoutEnabled && singleHostedCheckoutProvider !== "alipay" ? <Button type="button" variant="outline" className="w-full" onClick={() => handleHostedCheckout("alipay")} disabled={isActionDisabled}>
						{checkoutLoading && checkoutProvider === "alipay" ? t("common.redirecting") : t("billing.payWithAlipay")}
					</Button> : null}
				{stripeCardEnabled || stripeCheckoutEnabled ? <p className="text-xs text-muted-foreground text-center">
						{t("billing.stripeCheckoutMethods")}
					</p> : null}
				<InvoiceSettingsNote />
			</DialogFooter>
		</>;
}
function SuccessStep(t0) {
  const $ = _c(24);
  const {
    onClose
  } = t0;
  const {
    t
  } = useI18n();
  let t1;
  if ($[0] !== t) {
    t1 = t("billing.paymentSuccessful");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <DialogTitle>{t1}</DialogTitle>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("billing.creditsAdded");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <DialogDescription>{t3}</DialogDescription>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <DialogHeader>{t2}{t4}</DialogHeader>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("billing.thankYouPurchase");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <div className="py-4"><p>{t6}</p></div>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t("common.close");
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== onClose || $[18] !== t8) {
    t9 = <DialogFooter><Button onClick={onClose}>{t8}</Button></DialogFooter>;
    $[17] = onClose;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t5 || $[21] !== t7 || $[22] !== t9) {
    t10 = <>{t5}{t7}{t9}</>;
    $[20] = t5;
    $[21] = t7;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}
function PaymentStep({
  amount,
  organizationId,
  onBack,
  onSuccess,
  onCancel,
  loading,
  setLoading
}: {
  amount: number;
  organizationId?: string;
  onBack: () => void;
  onSuccess: () => Promise<void> | void;
  onCancel: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}) {
  const stripe = useStripeElements();
  const elements = useElements();
  const api = useApi();
  const {
    t
  } = useI18n();
  const {
    mutateAsync: topUpMutation
  } = api.useMutation("post", "/payments/create-payment-intent");
  const {
    mutateAsync: setupIntentMutation
  } = api.useMutation("post", "/payments/create-setup-intent");
  const [saveCard, setSaveCard] = useState(true);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setLoading(true);
    try {
      let stripePaymentMethodId: string | undefined;
      if (saveCard) {
        const {
          clientSecret: setupSecret
        } = await setupIntentMutation({});
        const setupResult = await stripe.confirmCardSetup(setupSecret, {
          payment_method: {
            card: elements.getElement(CardElement) as any
          }
        });
        if (setupResult.error) {
          toast.error(t("billing.errorSavingCard"), {
            description: setupResult.error.message
          });
          setLoading(false);
          return;
        }
        const setupPaymentMethod = setupResult.setupIntent?.payment_method;
        stripePaymentMethodId = typeof setupPaymentMethod === "string" ? setupPaymentMethod : setupPaymentMethod?.id;
      } else {
        const pmResult = await stripe.createPaymentMethod({
          type: "card",
          card: elements.getElement(CardElement) as any
        });
        if (pmResult.error) {
          toast.error(t("billing.error"), {
            description: pmResult.error.message ?? t("billing.couldNotReadCardDetails")
          });
          setLoading(false);
          return;
        }
        stripePaymentMethodId = pmResult.paymentMethod.id;
      }
      const {
        clientSecret
      } = await topUpMutation({
        body: {
          amount,
          stripePaymentMethodId,
          ...(organizationId ? {
            organizationId
          } : {})
        }
      });
      const result = await stripe.confirmCardPayment(clientSecret);
      if (result.error) {
        toast.error(t("billing.paymentFailed"), {
          description: result.error.message
        });
        setLoading(false);
      } else {
        await onSuccess();
      }
    } catch (error: any) {
      toast.error(t("billing.paymentFailed"), {
        description: error?.message ?? t("billing.paymentFailedDescription")
      });
      setLoading(false);
    }
  };
  return <>
			<DialogHeader>
				<DialogTitle>{t("billing.paymentDetails")}</DialogTitle>
				<DialogDescription>
					{t("billing.useCardDescription", {
          amount
        })}
				</DialogDescription>
			</DialogHeader>
			<form onSubmit={handleSubmit} className="space-y-4 py-4">
				<div className="space-y-2">
					<Label htmlFor="card-element">{t("billing.cardDetails")}</Label>
					<div className="border rounded-md p-3">
						<CardElement id="card-element" options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#424770",
                "::placeholder": {
                  color: "#aab7c4"
                }
              },
              invalid: {
                color: "#9e2146"
              }
            }
          }} />
					</div>
				</div>
				<div className="space-y-2">
					<div className="flex items-center space-x-2">
						<Checkbox id="save-card" checked={saveCard} onCheckedChange={checked => setSaveCard(checked as boolean)} />
						<Label htmlFor="save-card">{t("billing.saveCard")}</Label>
					</div>
				</div>
				<p className="text-xs text-muted-foreground">
					{t("billing.internationalCardsFeeNote")}
				</p>
				<DialogFooter className="flex space-x-2 justify-end">
					<Button type="button" variant="outline" onClick={onBack} disabled={loading}>
						{t("common.back")}
					</Button>
					<Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
						{t("common.cancel")}
					</Button>
					<Button type="submit" disabled={!stripe || loading}>
						{loading ? t("common.processing") : t("common.continue")}
					</Button>
				</DialogFooter>
				<InvoiceSettingsNote />
			</form>
		</>;
}
function SelectPaymentStep(t0) {
  const $ = _c(54);
  const {
    amount,
    paymentMethods,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    onUseSelected,
    onAddNew,
    onBack,
    onCancel
  } = t0;
  const {
    t
  } = useI18n();
  let t1;
  if ($[0] !== t) {
    t1 = t("billing.selectPaymentMethod");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <DialogTitle>{t1}</DialogTitle>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== amount || $[5] !== t) {
    t3 = t("billing.paymentMethodsDescription", {
      amount
    });
    $[4] = amount;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <DialogDescription>{t3}</DialogDescription>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <DialogHeader>{t2}{t4}</DialogHeader>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== paymentMethods || $[13] !== selectedPaymentMethod || $[14] !== setSelectedPaymentMethod || $[15] !== t) {
    let t7;
    if ($[17] !== selectedPaymentMethod || $[18] !== setSelectedPaymentMethod || $[19] !== t) {
      t7 = method => <div key={method.id} className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer ${selectedPaymentMethod === method.id ? "border-primary" : ""}`} onClick={() => setSelectedPaymentMethod(method.id)}><div className="flex items-center gap-3"><CreditCard className="h-5 w-5" /><div><p>{method.cardBrand} •••• {method.cardLast4}</p><p className="text-sm text-muted-foreground">{t("billing.expires", {
                month: method.expiryMonth ?? "",
                year: method.expiryYear ?? ""
              })}</p></div>{method.isDefault && <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">{t("billing.defaultPaymentMethod")}</span>}</div></div>;
      $[17] = selectedPaymentMethod;
      $[18] = setSelectedPaymentMethod;
      $[19] = t;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    t6 = paymentMethods.map(t7);
    $[12] = paymentMethods;
    $[13] = selectedPaymentMethod;
    $[14] = setSelectedPaymentMethod;
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Plus className="h-4 w-4" />;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t) {
    t8 = t("billing.addNewPaymentMethod");
    $[22] = t;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== onAddNew || $[25] !== t8) {
    t9 = <Button variant="outline" className="w-full flex items-center justify-center gap-2" onClick={onAddNew}>{t7}{t8}</Button>;
    $[24] = onAddNew;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t6 || $[28] !== t9) {
    t10 = <div className="space-y-4 py-4"><div className="space-y-2">{t6}{t9}</div></div>;
    $[27] = t6;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t) {
    t11 = t("common.back");
    $[30] = t;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== onBack || $[33] !== t11) {
    t12 = <Button type="button" variant="outline" onClick={onBack}>{t11}</Button>;
    $[32] = onBack;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t) {
    t13 = t("common.cancel");
    $[35] = t;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== onCancel || $[38] !== t13) {
    t14 = <Button type="button" variant="outline" onClick={onCancel}>{t13}</Button>;
    $[37] = onCancel;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const t15 = !selectedPaymentMethod;
  let t16;
  if ($[40] !== t) {
    t16 = t("common.continue");
    $[40] = t;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== onUseSelected || $[43] !== t15 || $[44] !== t16) {
    t17 = <Button type="button" onClick={onUseSelected} disabled={t15}>{t16}</Button>;
    $[42] = onUseSelected;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t12 || $[47] !== t14 || $[48] !== t17) {
    t18 = <DialogFooter className="flex space-x-2 justify-end">{t12}{t14}{t17}</DialogFooter>;
    $[46] = t12;
    $[47] = t14;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t10 || $[51] !== t18 || $[52] !== t5) {
    t19 = <>{t5}{t10}{t18}</>;
    $[50] = t10;
    $[51] = t18;
    $[52] = t5;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  return t19;
}
function ConfirmPaymentStep({
  amount,
  paymentMethodId,
  organizationId,
  onSuccess,
  onBack,
  onCancel,
  loading,
  setLoading
}: {
  amount: number;
  paymentMethodId: string;
  organizationId?: string;
  onSuccess: () => Promise<void> | void;
  onBack: () => void;
  onCancel: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}) {
  const api = useApi();
  const {
    t
  } = useI18n();
  const {
    mutateAsync: topUpMutation
  } = api.useMutation("post", "/payments/top-up-with-saved-method");
  const {
    data: feeData,
    isLoading: feeDataLoading
  } = api.useQuery("post", "/payments/calculate-fees", {
    body: {
      amount,
      paymentMethodId
    }
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await topUpMutation({
        body: {
          amount,
          paymentMethodId,
          ...(organizationId ? {
            organizationId
          } : {})
        }
      });
      await onSuccess();
    } catch (error: any) {
      toast.error(t("billing.paymentFailed"), {
        description: error?.message ?? t("billing.paymentFailedDescription")
      });
      setLoading(false);
    }
  };
  return <>
			<DialogHeader>
				<DialogTitle>{t("billing.confirmPayment")}</DialogTitle>
				<DialogDescription>
					{t("billing.reviewPaymentDetails")}
				</DialogDescription>
			</DialogHeader>
			<form onSubmit={handleSubmit} className="space-y-4 py-4">
				<div className="border rounded-lg p-4">
					<p className="font-medium mb-3">{t("billing.paymentSummary")}</p>
					{feeDataLoading ? <div className="flex items-center justify-center py-4">
							<div className="h-5 w-5 animate-spin border-2 border-muted-foreground border-t-transparent rounded-full" />
							<span className="ml-2 text-sm text-muted-foreground">
								{t("billing.calculatingFees")}
							</span>
						</div> : feeData ? <div className="space-y-2 text-sm">
							<div className="flex justify-between">
								<span>{t("billing.credits")}</span>
								<span>${feeData.baseAmount.toFixed(2)}</span>
							</div>
							<div className="flex justify-between">
								<span>{t("billing.platformFee")}</span>
								<span>${feeData.platformFee.toFixed(2)}</span>
							</div>
							{feeData.internationalFee > 0 ? <div className="flex justify-between">
									<span>{t("billing.internationalCardFee")}</span>
									<span>${feeData.internationalFee.toFixed(2)}</span>
								</div> : null}
							<div className="border-t pt-2 flex justify-between font-medium">
								<span>{t("billing.total")}</span>
								<span>${feeData.totalAmount.toFixed(2)}</span>
							</div>
						</div> : <p className="text-sm text-muted-foreground">
							{t("billing.amountSummary", {
            amount
          })}
						</p>}
				</div>
				<DialogFooter className="flex space-x-2 justify-end">
					<Button type="button" variant="outline" onClick={onBack} disabled={loading}>
						{t("common.back")}
					</Button>
					<Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
						{t("common.cancel")}
					</Button>
					<Button type="submit" disabled={loading || feeDataLoading}>
						{loading ? t("common.processing") : t("billing.payAmount", {
            amount: feeData ? feeData.totalAmount.toFixed(2) : amount
          })}
					</Button>
				</DialogFooter>
				<InvoiceSettingsNote />
			</form>
		</>;
}
function InvoiceSettingsNote() {
  const $ = _c(4);
  const {
    t
  } = useI18n();
  let t0;
  if ($[0] !== t) {
    t0 = t("billing.invoiceSettingsNote");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <p className="text-center text-[11px] leading-relaxed text-muted-foreground">{t0}</p>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}